import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Crown, MagnifyingGlass, X, CheckCircle, ArrowsClockwise, Warning, Key, Clock } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function fmt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(); } catch { return d; }
}

const MONTH_OPTIONS = [1, 2, 3, 6, 12];

function daysLeft(d) {
  if (!d) return null;
  const ms = new Date(d).getTime() - Date.now();
  return Math.ceil(ms / 86400000);
}

// Months dropdown + grant button. Used on each search-result row.
function TimedGrantControl({ email, busy, onGrant, label = "Grant Timed Free" }) {
  const [months, setMonths] = useState("1");
  return (
    <div className="inline-flex items-center gap-1.5">
      <Select value={months} onValueChange={setMonths}>
        <SelectTrigger className="h-8 w-[108px] text-xs" data-testid={`admin-timed-months-${email}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {MONTH_OPTIONS.map((m) => (
            <SelectItem key={m} value={String(m)} data-testid={`admin-timed-months-${email}-${m}`}>
              {m} month{m > 1 ? "s" : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={() => onGrant(email, Number(months))}
        className="border-sky-500/40 text-sky-400 hover:bg-sky-500/10"
        data-testid={`admin-grant-timed-${email}`}
      >
        <Clock size={12} weight="fill" className="mr-1" /> {label}
      </Button>
    </div>
  );
}

// Compact form for pasting a fresh Dhan token. Lives inside the Dhan card
// on /admin. Because Dhan caps tokens at 24h we surface this prominently
// so admins can refresh every morning without a code change / Republish.
function DhanTokenForm({ dhan, onSaved }) {
  const [token, setToken] = useState("");
  const [clientId, setClientId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // If credentials already exist, don't preload the token (sensitive).
    // But preload the client ID since it's not sensitive and rarely changes.
    // Backend health doesn't return the raw client id — leave both blank so
    // admin re-enters intentionally.
    setToken(""); setClientId("");
  }, [dhan?.updated_at]);

  const save = async () => {
    if (!token.trim() || !clientId.trim()) {
      toast.error("Paste both the access token and client ID");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/dhan/config", {
        access_token: token.trim(),
        client_id: clientId.trim(),
      });
      toast.success(`Dhan token saved — expires ${new Date(data.token_expires_at).toLocaleString()}`);
      setToken(""); setClientId("");
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed — check the token is valid");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Forget the stored Dhan token?\nMCX charts will stop until you paste a new one.")) return;
    setBusy(true);
    try {
      await api.delete("/dhan/config");
      toast.success("Dhan token cleared");
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-primary/30 bg-primary/5 p-4"
      data-testid="dhan-token-form">
      <div className="flex items-center gap-2 mb-3">
        <Key size={14} weight="fill" className="text-primary" />
        <p className="text-[11px] tracking-[0.25em] uppercase text-primary font-semibold">
          {dhan?.configured ? "Refresh token" : "Paste your Dhan credentials"}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_200px] gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Access Token (JWT)</label>
          <Input
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="eyJ0eXAiOiJKV1QiLC..."
            className="mt-1 font-mono text-xs"
            data-testid="dhan-token-input"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Client ID (UCC)</label>
          <Input
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            placeholder="1100XXXXXX"
            className="mt-1 font-mono text-xs"
            data-testid="dhan-client-input"
          />
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button size="sm" onClick={save} disabled={busy} data-testid="dhan-save-btn">
          {busy ? "Saving…" : dhan?.configured ? "Update Token" : "Save & Activate"}
        </Button>
        {dhan?.configured && (
          <Button size="sm" variant="ghost" onClick={clear} disabled={busy} data-testid="dhan-clear-btn">
            <X size={12} className="mr-1" /> Clear
          </Button>
        )}
        <span className="text-[10px] text-muted-foreground ml-auto">
          Dhan tokens are 24h max — refresh here each morning, no Republish needed.
        </span>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, refresh } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [lifetime, setLifetime] = useState([]);
  const [timed, setTimed] = useState([]);
  const [busyEmail, setBusyEmail] = useState(null);
  const [searching, setSearching] = useState(false);
  const [dhan, setDhan] = useState(null);
  const [dhanBusy, setDhanBusy] = useState(false);

  const loadDhan = useCallback(async () => {
    try {
      const { data } = await api.get("/dhan/health");
      setDhan(data);
    } catch (_) {
      setDhan({ configured: false, active_contracts: {} });
    }
  }, []);

  const refreshDhan = async () => {
    setDhanBusy(true);
    try {
      const { data } = await api.post("/dhan/refresh-contracts");
      setDhan((prev) => ({ ...(prev || {}), active_contracts: data.active_contracts }));
      toast.success(`Refreshed ${Object.keys(data.active_contracts || {}).length} MCX contracts`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setDhanBusy(false);
    }
  };

  const loadLifetime = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/lifetime-users");
      setLifetime(data.users || []);
    } catch (e) {
      // Non-admin lands here — bounce
      toast.error(e?.response?.data?.detail || "Failed to load lifetime users");
    }
  }, []);

  const loadTimed = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/timed-users");
      setTimed(data.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load timed users");
    }
  }, []);

  useEffect(() => { loadLifetime(); loadTimed(); loadDhan(); }, [loadLifetime, loadTimed, loadDhan]);

  const search = async () => {
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get(`/admin/users/search?q=${encodeURIComponent(q.trim())}`);
      setResults(data.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const grant = async (email) => {
    setBusyEmail(email);
    try {
      await api.post("/admin/grant-lifetime", { email });
      toast.success(`Lifetime granted to ${email}`);
      await Promise.all([loadLifetime(), search(), refresh()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grant failed");
    } finally {
      setBusyEmail(null);
    }
  };

  const revoke = async (email) => {
    if (!window.confirm(`Revoke lifetime access from ${email}?\nThey'll get a fresh 14-day trial instead.`)) return;
    setBusyEmail(email);
    try {
      await api.post("/admin/revoke-lifetime", { email });
      toast.success(`Lifetime revoked from ${email}`);
      await Promise.all([loadLifetime(), search()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    } finally {
      setBusyEmail(null);
    }
  };

  const grantTimed = async (email, months) => {
    setBusyEmail(email);
    try {
      const { data } = await api.post("/admin/grant-timed", { email, months });
      toast.success(`${months}-month free access granted to ${email} (until ${fmt(data.expires_at)})`);
      await Promise.all([loadTimed(), loadLifetime(), search(), refresh()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grant failed");
    } finally {
      setBusyEmail(null);
    }
  };

  const extendTimed = async (email, months) => {
    setBusyEmail(email);
    try {
      const { data } = await api.post("/admin/extend-timed", { email, months });
      toast.success(`Extended ${email} by ${months} month(s) — now until ${fmt(data.expires_at)}`);
      await Promise.all([loadTimed(), search()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extend failed");
    } finally {
      setBusyEmail(null);
    }
  };

  const revokeTimed = async (email) => {
    if (!window.confirm(`Revoke timed free access from ${email}?\nThey drop to the free tier immediately.`)) return;
    setBusyEmail(email);
    try {
      await api.post("/admin/revoke-timed", { email });
      toast.success(`Timed access revoked from ${email}`);
      await Promise.all([loadTimed(), search()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    } finally {
      setBusyEmail(null);
    }
  };

  // Guard — non-admins get bounced. Waits for user to hydrate first.
  if (user && !user.is_admin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8" data-testid="admin-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 grid place-items-center">
          <Crown size={20} weight="fill" className="text-amber-400" />
        </div>
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Admin</p>
          <h1 className="font-display text-3xl font-black">Free Access</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Grant lifetime or timed free access for any user. Signed in as <span className="font-medium text-foreground">{user?.email}</span>.
          </p>
        </div>
        <div className="ml-auto">
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              if (!window.confirm("Wipe cached prop-sim prices?\nNext quote will pull fresh from the live feed. Use this after a Republish if stored prices drift from TradingView.")) return;
              try {
                const { data } = await api.post("/admin/prices/reset");
                toast.success(`Cleared ${data.cleared} stale prices — next tick refreshes from live feed`);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Reset failed");
              }
            }}
            data-testid="admin-reset-prices"
          >
            Reset Sim Prices
          </Button>
        </div>
      </div>

      {/* Dhan (MCX) integration status card */}
      <div className="rounded-2xl border border-border bg-card p-5" data-testid="admin-dhan-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] tracking-[0.25em] uppercase text-muted-foreground">Dhan · MCX Data API</p>
            <h3 className="font-display text-xl font-bold mt-1">Indian Commodity Charts</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Powers MCX Gold, Silver, Crude Oil charts on <code className="bg-secondary/40 px-1 py-0.5 rounded">/charts</code>.
              Requires Dhan Data API subscription (~₹499/mo).
            </p>
          </div>
          {dhan?.configured ? (
            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
              <CheckCircle size={12} weight="fill" /> Connected
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-orange-500/15 text-orange-300 border border-orange-500/30">
              <Warning size={12} weight="fill" /> Not configured
            </span>
          )}
        </div>

        {!dhan?.configured && (
          <div className="mt-4 rounded-lg bg-secondary/40 border border-border p-3 text-xs text-muted-foreground space-y-2">
            <p>To activate MCX charts:</p>
            <ol className="list-decimal list-inside space-y-1 pl-1">
              <li>Subscribe to the Data API on <a href="https://dhanhq.co/trading-apis" target="_blank" rel="noreferrer" className="text-primary underline">dhanhq.co/trading-apis</a></li>
              <li>Generate an access token from Dhan → API Access</li>
              <li>Paste it below (no Republish needed — hot-swaps live)</li>
            </ol>
          </div>
        )}

        {/* Token entry form — paste a fresh 24h token here each morning */}
        <DhanTokenForm dhan={dhan} onSaved={loadDhan} />

        {dhan?.configured && dhan?.source === "db" && dhan?.hours_left !== null && dhan?.hours_left !== undefined && (
          <div className={`mt-4 flex items-center gap-3 rounded-lg px-3 py-2 text-xs border ${
            dhan.hours_left <= 2
              ? "bg-red-500/10 border-red-500/30 text-red-300"
              : dhan.hours_left <= 6
              ? "bg-amber-500/10 border-amber-500/30 text-amber-300"
              : "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
          }`}
            data-testid="dhan-token-expiry">
            <div className={`w-2 h-2 rounded-full ${
              dhan.hours_left <= 2 ? "bg-red-400" : dhan.hours_left <= 6 ? "bg-amber-400" : "bg-emerald-400"
            } animate-pulse`} />
            <span className="font-mono tracking-wider uppercase text-[10px]">Token TTL</span>
            <span className="font-semibold">
              {dhan.hours_left > 0
                ? `${dhan.hours_left.toFixed(1)}h left`
                : "Expired — paste a fresh token below"}
            </span>
            <span className="text-muted-foreground ml-auto">Updated {dhan.updated_at ? new Date(dhan.updated_at).toLocaleString() : "—"}</span>
          </div>
        )}

        {dhan?.configured && (
          <>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              {(dhan.supported_symbols || []).map((s) => {
                const c = dhan.active_contracts?.[s.underlying];
                return (
                  <div key={s.symbol}
                    className="rounded-xl border border-border bg-secondary/20 p-3"
                    data-testid={`admin-dhan-contract-${s.symbol}`}>
                    <p className="text-[10px] tracking-widest uppercase text-muted-foreground">{s.symbol}</p>
                    <p className="text-sm font-semibold mt-0.5">{s.name}</p>
                    {c ? (
                      <div className="mt-2 text-xs space-y-0.5">
                        <p className="font-mono text-foreground truncate" title={c.trading_symbol}>{c.trading_symbol}</p>
                        <p className="text-muted-foreground">ID {c.security_id} · exp {c.expiry}</p>
                      </div>
                    ) : (
                      <p className="text-xs text-orange-300 mt-2">No active contract</p>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-4">
              <Button size="sm" variant="outline" onClick={refreshDhan} disabled={dhanBusy}
                data-testid="admin-dhan-refresh">
                <ArrowsClockwise size={12} className={`mr-1.5 ${dhanBusy ? "animate-spin" : ""}`} />
                {dhanBusy ? "Refreshing…" : "Refresh contract master"}
              </Button>
              <p className="text-[10px] text-muted-foreground mt-2">
                Pulls the latest scrip master from Dhan and picks the nearest-expiry FUTCOM for each underlying. Run this before every monthly contract rollover.
              </p>
            </div>
          </>
        )}
      </div>

      {/* Search + grant */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <p className="text-[11px] tracking-[0.25em] uppercase text-muted-foreground mb-3">Find a user</p>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="Search by email (any part matches)…"
              className="pl-9"
              data-testid="admin-search-input"
            />
          </div>
          <Button onClick={search} disabled={searching} data-testid="admin-search-btn">
            {searching ? "Searching…" : "Search"}
          </Button>
        </div>

        {results.length > 0 && (
          <div className="mt-4 border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40">
                <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Plan</th>
                  <th className="px-3 py-2">Access ends</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.id} className="border-t border-border" data-testid={`admin-user-${r.email}`}>
                    <td className="px-3 py-2 font-mono text-xs">{r.email}</td>
                    <td className="px-3 py-2">{r.name || "—"}</td>
                    <td className="px-3 py-2">
                      {r.is_lifetime ? (
                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">
                          <Crown size={10} weight="fill" /> Lifetime
                        </span>
                      ) : r.is_timed ? (
                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-400 border border-sky-500/30">
                          <Clock size={10} weight="fill" /> Timed free
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">{r.plan_id || r.status || "trial"}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {fmt(r.current_period_end)}
                      {r.is_timed && (
                        <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full border ${
                          (daysLeft(r.current_period_end) ?? 0) > 0
                            ? "bg-sky-500/10 text-sky-300 border-sky-500/30"
                            : "bg-red-500/10 text-red-300 border-red-500/30"
                        }`} data-testid={`admin-timed-daysleft-${r.email}`}>
                          {(daysLeft(r.current_period_end) ?? 0) > 0
                            ? `${daysLeft(r.current_period_end)}d left`
                            : "expired"}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {r.is_lifetime ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => revoke(r.email)}
                          disabled={busyEmail === r.email}
                          data-testid={`admin-revoke-${r.email}`}
                        >
                          <X size={12} className="mr-1" /> Revoke
                        </Button>
                      ) : r.is_timed ? (
                        <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
                          <TimedGrantControl
                            email={r.email}
                            busy={busyEmail === r.email}
                            onGrant={extendTimed}
                            label="Extend"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => revokeTimed(r.email)}
                            disabled={busyEmail === r.email}
                            data-testid={`admin-revoke-timed-${r.email}`}
                          >
                            <X size={12} className="mr-1" /> Revoke
                          </Button>
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
                          <Button
                            size="sm"
                            onClick={() => grant(r.email)}
                            disabled={busyEmail === r.email}
                            className="bg-amber-500 hover:bg-amber-400 text-black"
                            data-testid={`admin-grant-${r.email}`}
                          >
                            <Crown size={12} weight="fill" className="mr-1" /> Grant Lifetime
                          </Button>
                          <TimedGrantControl
                            email={r.email}
                            busy={busyEmail === r.email}
                            onGrant={grantTimed}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {q && !searching && results.length === 0 && (
          <p className="mt-3 text-xs text-muted-foreground">No users match “{q}”.</p>
        )}
      </div>

      {/* Existing lifetime holders */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] tracking-[0.25em] uppercase text-muted-foreground">
            Lifetime holders ({lifetime.length})
          </p>
          <span className="text-[11px] text-muted-foreground">Auto-updates after each grant / revoke</span>
        </div>

        {lifetime.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground border border-dashed border-border rounded-xl">
            No lifetime users yet. Search above and click <span className="text-amber-400 font-medium">Grant Lifetime</span> to add the first one.
          </div>
        ) : (
          <div className="border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40">
                <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Granted</th>
                  <th className="px-3 py-2">Granted by</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {lifetime.map((r) => (
                  <tr key={r.id} className="border-t border-border" data-testid={`admin-lifetime-${r.email}`}>
                    <td className="px-3 py-2 font-mono text-xs flex items-center gap-1.5">
                      <CheckCircle size={12} weight="fill" className="text-emerald-400" /> {r.email}
                    </td>
                    <td className="px-3 py-2">{r.name || "—"}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{fmt(r.granted_at)}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{r.granted_by || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => revoke(r.email)}
                        disabled={busyEmail === r.email}
                        data-testid={`admin-lifetime-revoke-${r.email}`}
                      >
                        <X size={12} className="mr-1" /> Revoke
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Timed free-access holders */}
      <div className="rounded-2xl border border-border bg-card p-5" data-testid="admin-timed-card">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] tracking-[0.25em] uppercase text-muted-foreground">
            Timed free access ({timed.length})
          </p>
          <span className="text-[11px] text-muted-foreground">Expires automatically — no action needed</span>
        </div>

        {timed.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground border border-dashed border-border rounded-xl">
            No timed grants yet. Search above, pick a duration and click <span className="text-sky-400 font-medium">Grant Timed Free</span>.
          </div>
        ) : (
          <div className="border border-border rounded-xl overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-secondary/40">
                <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Expires</th>
                  <th className="px-3 py-2">Granted by</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {timed.map((r) => {
                  const dl = daysLeft(r.expires_at);
                  return (
                    <tr key={r.id} className="border-t border-border" data-testid={`admin-timed-${r.email}`}>
                      <td className="px-3 py-2 font-mono text-xs flex items-center gap-1.5">
                        <Clock size={12} weight="fill" className={r.expired ? "text-red-400" : "text-sky-400"} /> {r.email}
                      </td>
                      <td className="px-3 py-2">{r.name || "—"}</td>
                      <td className="px-3 py-2 text-xs">
                        <span className="text-muted-foreground">{fmt(r.expires_at)}</span>
                        <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full border ${
                          r.expired
                            ? "bg-red-500/10 text-red-300 border-red-500/30"
                            : "bg-sky-500/10 text-sky-300 border-sky-500/30"
                        }`}>
                          {r.expired ? "expired" : `${dl}d left`}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{r.granted_by || "—"}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
                          <TimedGrantControl
                            email={r.email}
                            busy={busyEmail === r.email}
                            onGrant={extendTimed}
                            label="Extend"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => revokeTimed(r.email)}
                            disabled={busyEmail === r.email}
                            data-testid={`admin-timed-revoke-${r.email}`}
                          >
                            <X size={12} className="mr-1" /> Revoke
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
